import { useEffect, useMemo, useState } from "react";
import type { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import type { ActivitySeries } from "@common/schemas/domains/activity/activity-timeline.schema";
import type { HistoryFilter } from "@common/types/auditLog";
import type { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import type { DensityChartWindow } from "@design-system/DensityChart/DensityChart.lib";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { toTimelineListFilter } from "./activityTimelineSeries.lib";
import type { StoredLastVisit } from "./lastVisit/lastVisit.lib";
import type { StoredActivityChartDensity } from "./useActivityChartDensity";
import { useActivityChartDensity } from "./useActivityChartDensity";
import { useActivityTimeline } from "./useActivityTimeline";

export interface UseActivityTimelinePageParams<Filter extends HistoryFilter> {
    dateRange: DateOnlyDateRange;
    /** This page's stored last visit, wherever the page keeps it. */
    stored: StoredLastVisit | undefined;
    save: (visit: StoredLastVisit) => void;
    /** This page's saved chart density, wherever the page keeps it. */
    storedDensity: StoredActivityChartDensity;
    /** When history starts, e.g. the workspace's creation. */
    historyStartAt?: Date;
    /** The toolbar's filters, as the page's History list takes them. */
    apiFilters: Filter;
    outcome: ActivityOutcomeKind | undefined;
    series: readonly ActivitySeries[] | undefined;
}

/**
 * An Activity page's timeline wiring behind the `activityTimeline` flag: the
 * list narrowed to the window, outcome and series, and the visit
 * remembered. With the flag off the list keeps the toolbar's filters. The
 * window is the date range, so the page's date setter moves it;
 * `previewWindow` is where a drag would drop it, for the date button only.
 * `isSinceLastVisitWindow` is when the list shows only the default "Since
 * your last visit" days, which isn't a filter the user picked.
 */
export function useActivityTimelinePage<Filter extends HistoryFilter>({
    dateRange,
    stored,
    save,
    storedDensity,
    historyStartAt,
    apiFilters,
    outcome,
    series,
}: UseActivityTimelinePageParams<Filter>) {
    const { activityTimeline: isActivityTimelineEnabled } = useTypedFlags();
    const timeline = useActivityTimeline({
        dateRange,
        stored,
        save,
        historyStartAt,
    });
    const { rememberVisit } = timeline;
    const chartDensity = useActivityChartDensity(storedDensity);
    const [previewWindow, setPreviewWindow] =
        useState<DensityChartWindow | null>(null);

    useEffect(() => {
        if (isActivityTimelineEnabled) {
            rememberVisit();
        }
    }, [isActivityTimelineEnabled, rememberVisit]);

    const listFilter = useMemo(
        () =>
            isActivityTimelineEnabled
                ? toTimelineListFilter({
                      filter: apiFilters,
                      outcome,
                      series,
                      window: timeline.range.window,
                  })
                : apiFilters,
        [
            isActivityTimelineEnabled,
            apiFilters,
            outcome,
            series,
            timeline.range.window,
        ],
    );

    const hasActiveTimelineFilters =
        isActivityTimelineEnabled &&
        (outcome !== undefined || (series?.length ?? 0) > 0);
    const isSinceLastVisitWindow =
        isActivityTimelineEnabled && timeline.range.isLastVisit;

    return {
        isActivityTimelineEnabled,
        timeline,
        listFilter,
        hasActiveTimelineFilters,
        isSinceLastVisitWindow,
        previewWindow: isActivityTimelineEnabled ? previewWindow : null,
        setPreviewWindow,
        chartDensity,
    };
}
